import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';

import { useEventTracking } from '../../../services/statsig';
import type { BillingInterval, BundleWithStatus } from '../types';

import { AddOnCard } from './AddOnCard';
import { PlanSummary } from './PlanSummary';

interface AddOnSelectionStepProps {
  selectedTier: BundleWithStatus | null;
  addOns: BundleWithStatus[];
  selectedAddOnIds: Set<string>;
  billingInterval: BillingInterval;
  onToggleAddOn: (addOnId: string) => void;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  onChangeTier: () => void;
  onNext: () => void;
  isSingleTier?: boolean;
}

/**
 * Calculate grid position for an add-on card based on index and total count.
 * Pattern: 2 add-ons per row. If total is odd, last add-on spans both columns.
 */
const getAddOnGridPosition = (
  index: number,
  totalCount: number,
): { gridColumn: string; gridRow: string } => {
  const row = Math.floor(index / 2) + 1;
  const col = (index % 2) + 1;
  const isLastItem = index === totalCount - 1;
  const isOddTotal = totalCount % 2 === 1;
  const shouldSpan = isLastItem && isOddTotal;

  if (shouldSpan) {
    return {
      gridColumn: '1 / 3',
      gridRow: `${row}`,
    };
  }

  return {
    gridColumn: `${col}`,
    gridRow: `${row}`,
  };
};

export const AddOnSelectionStep: React.FC<AddOnSelectionStepProps> = ({
  selectedTier,
  addOns,
  selectedAddOnIds,
  billingInterval,
  onToggleAddOn,
  onBillingIntervalChange,
  onChangeTier,
  onNext,
  isSingleTier = false,
}) => {
  const { subscription } = useEventTracking();
  const selectedAddOns = addOns.filter(addOn => selectedAddOnIds.has(addOn.bundle_id));

  // Calculate total rows needed for dynamic grid
  const totalRows = Math.ceil(addOns.length / 2);

  const handleChangeTier = () => {
    subscription.changeTierClicked({
      current_tier_id: selectedTier?.bundle_id,
      current_tier_name: selectedTier?.name,
      addon_count: selectedAddOnIds.size,
    });
    onChangeTier();
  };

  const handleContinue = () => {
    subscription.addonsContinueClicked({
      tier_id: selectedTier?.bundle_id,
      addon_count: selectedAddOnIds.size,
      billing_interval: billingInterval,
    });
    onNext();
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1400px',
        mx: 'auto',
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Header for single-tier mode (replaces tier selection step) */}
      {isSingleTier && (
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            Build your Plan
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
            Flexible options for your practice&apos;s every need
          </Typography>
        </Box>
      )}

      {/* Selected Tier Display (hidden in single-tier mode) */}
      {!isSingleTier && selectedTier && (
        <Card
          sx={{
            mb: 4,
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.light',
              boxShadow: 2,
            },
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              '&:last-child': {
                pb: 3,
              },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Selected tier
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedTier.name}
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{ fontWeight: 700, color: 'primary.main' }}
                >
                  $
                  {(
                    (billingInterval === 'month'
                      ? selectedTier.monthly_unit_amount
                      : Math.round(selectedTier.yearly_unit_amount / 12)) / 100
                  ).toFixed(0)}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: 'text.secondary', ml: 0.5 }}
                  >
                    /mo
                  </Typography>
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={handleChangeTier}
              sx={{
                minWidth: '100px',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Change
            </Button>
          </CardContent>
        </Card>
      )}

      {/* CSS Grid layout: Add-ons and Summary */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(3, 1fr)',
          },
          gridTemplateRows: {
            xs: 'auto',
            lg: 'auto auto',
          },
          gap: 3,
        }}
      >
        {/* Add-on Cards with dynamic grid positioning */}
        {addOns.map((addOn, index) => {
          const position = getAddOnGridPosition(index, addOns.length);

          return (
            <Box
              key={addOn.bundle_id}
              sx={{
                gridColumn: { xs: '1', lg: position.gridColumn },
                gridRow: { xs: 'auto', lg: position.gridRow },
              }}
            >
              <AddOnCard
                bundle={addOn}
                billingInterval={billingInterval}
                isSelected={selectedAddOnIds.has(addOn.bundle_id)}
                onToggle={onToggleAddOn}
              />
            </Box>
          );
        })}

        {/* Plan Summary: right column, spanning all rows */}
        <Box
          sx={{
            gridColumn: { xs: '1', lg: '3' },
            gridRow: { xs: 'auto', lg: `1 / ${totalRows + 1}` },
            height: { lg: '100%' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <PlanSummary
            selectedTier={selectedTier}
            selectedAddOns={selectedAddOns}
            billingInterval={billingInterval}
            onBillingIntervalChange={onBillingIntervalChange}
            onContinue={handleContinue}
            showContinueButton={true}
          />
        </Box>
      </Box>
    </Box>
  );
};
